import { Component, ElementRef, OnDestroy } from '@angular/core';
import {
  combineLatest,
  fromEvent,
  map,
  merge,
  Observable,
  skip,
  Subject,
  takeUntil,
} from 'rxjs';

import { StateManagerProjectService } from '@state-manager';
import { ViewWindowIndex } from '@viewer/data';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-base-tool',
  template: '',
})
export class BaseToolComponent implements OnDestroy {
  public isDisabled$!: Observable<boolean>;

  private _menuTrigger!: MatMenuTrigger;
  private _childElementRef!: ElementRef<HTMLElement>;
  private _menuBackdropRootUnsbscribe$!: Subject<void>;
  private _rootUnsubscribe$ = new Subject<void>();

  constructor(
    protected _stateManagerProjectService: StateManagerProjectService
  ) {
    this.isDisabled$ = combineLatest([
      this._stateManagerProjectService.isViewerLoaded$,
      this._stateManagerProjectService.currentSelectedWindow$,
      this._stateManagerProjectService.isProcessOfResetDiff$,
    ]).pipe(
      map(
        ([isViewerLoaded, currentSelectedWindow, isProcessOfResetDiff]) =>
          !isViewerLoaded ||
          [ViewWindowIndex.Empty, ViewWindowIndex.Model3D].includes(
            currentSelectedWindow
          ) ||
          isProcessOfResetDiff[`viewer${currentSelectedWindow}`]
      )
    );
  }

  ngOnDestroy(): void {
    this._rootUnsubscribe$.next();
    this._rootUnsubscribe$.complete();
    if (this._menuBackdropRootUnsbscribe$) {
      this._menuBackdropRootUnsbscribe$.next();
      this._menuBackdropRootUnsbscribe$.complete();
    }
  }

  protected initializeMenuBackdrop(
    trigger: MatMenuTrigger,
    elementRef: ElementRef<HTMLElement>
  ): void {
    this._menuTrigger = trigger;
    this._childElementRef = elementRef;

    this._menuTrigger.menuOpened
      .pipe(takeUntil(this._rootUnsubscribe$))
      .subscribe(() => {
        this._initializeMenuBackdropCloseSubscriptions();
      });

    this._menuTrigger.menuClosed
      .pipe(takeUntil(this._rootUnsubscribe$))
      .subscribe(() => {
        this._menuBackdropRootUnsbscribe$.next();
        this._menuBackdropRootUnsbscribe$.complete();
      });
  }

  private _initializeMenuBackdropCloseSubscriptions(): void {
    this._menuBackdropRootUnsbscribe$ = new Subject<void>();

    this._stateManagerProjectService.currentSelectedWindow$
      .pipe(skip(1), takeUntil(this._menuBackdropRootUnsbscribe$))
      .subscribe(() => {
        this._menuTrigger.closeMenu();
      });

    merge(
      fromEvent<MouseEvent>(document, 'contextmenu'),
      fromEvent<MouseEvent>(document, 'click')
    )
      .pipe(takeUntil(this._menuBackdropRootUnsbscribe$))
      .subscribe((event) => {
        if (!this._childElementRef || !this._menuTrigger) {
          return;
        }

        let closeMenu = true;
        this._childElementRef.nativeElement.childNodes.forEach((childNode) => {
          if (event.composedPath().includes(childNode)) {
            closeMenu = false;
          }
        });

        if (!closeMenu) {
          return;
        }

        this._menuTrigger.closeMenu();
      });
  }
}
